// Live solver: one board that tells a story and breathes.
//
//   const wall = buildWall(versions, measure)
//   const view = wallAt(wall, tSeconds)
//
// Story layer (from the planner's frame):
//   - sections: each is a banner lettered at the top of its region; every
//     chunk belongs to a section and is placed ONLY inside that region — no
//     spill, so placement keeps its meaning (sky/summit/ground…)
//   - spine: one path through the 4–6 chunks that are the meeting's arc
//   - reframe (at most once): the planner may replace metaphor + sections
//     once the meeting's shape is clear; the board reflows to the new frame,
//     old banners fade, the anchor art swaps
//   - consolidation: retired chunks fade out, their items reappear in the
//     chunk they were folded into
// Breathing (stability as a default, not a law):
//   - keep everything; fit new boxes into gaps → tidy (re-pack same scale)
//     → shrink ×0.93 per step; boxes drift with an animated transform
//   - chunks that gain items grow in place (width fixed)
// Reveal follows the meeting clock; frames are pencilled until the chunk's
// last item + 45 s, then inked (second pass).

import { PANEL, getTemplate, regionPolygon } from './templates.js';

import { anchorCore, bbox, boardPoly, chunkMaxW, chunkIntent, chunkReach, flowAnchor, inflate, layoutChunk, layoutTitle, onCorner, overlaps, place, placeNear, rectInPoly, stickerSize } from './packer.js';
import { NO_REACH } from './motifs.js';

// SCENE_DEBUG: set `globalThis.SCENE_DEBUG = true` (bench.mjs does) for a
// per-version solver trace. The typeof guard reads it without naming
// `globalThis`, which CRA's eslint env predated; the `global` comment is
// the one no-undef escape both that linter and ours accept (tapp vendors
// this file byte for byte).
/* global SCENE_DEBUG */
const DEBUG = () => typeof SCENE_DEBUG !== 'undefined' && SCENE_DEBUG;

const SECOND_PASS_DELAY = 45;
const ITEM_GAP = 8;
const SCALE_STEP = 0.93;
// The body register never sets smaller than this share of the panel's
// height (TT-1215, the scene's legible floor — TT-1168 note 6: trim
// content, never size). Every shrink, global or per box, stops here; a
// version that still does not fit leaves its least important new chunk off
// the board instead.
const TYPE_FLOOR = 0.015;
// At the floor, after the new chunks, this many old ones may leave to make room.
const MAX_MAKE_ROOM = 3;
// A version's budget is a count of layout attempts (a shrink ladder is ~20,
// a drop or make-room round a few more), so a cold page and a warm one
// solve the same series the same way (TT-1266: on a first load seven of
// seventeen versions blew a 1.5 s clock and were left off). The clock is
// a backstop against a pathological version only.
// The series as a whole is clocked too: past SERIES_BACKSTOP_MS the rest of
// a replay is left off, so a pathological series never holds the page.
const MAX_LAYOUTS = 400, BACKSTOP_MS = 6000, SERIES_BACKSTOP_MS = 20000;

export const edgeKey = (e) => `${e.from}>${e.to}`;

export function bodyPxFor(expectedMinutes, panel = PANEL) {
  const m = Math.max(15, Math.min(120, expectedMinutes || 45));
  return Math.max(panel.h * 0.016, Math.min(panel.h * 0.034, panel.h * 0.024 * Math.sqrt(45 / m)));
}

// Long lists wrap into columns (a scribe writes a second column rather than
// running off the bottom of the region). Lines carry column offsets.
export function layoutColumns(chunk, bodyPx, colW, measure, maxH = Infinity, maxW = Infinity) {
  // fewest columns (≤ 3) whose layout fits under maxH without exceeding maxW;
  // if nothing satisfies both, the shortest layout that respects the width
  let best = null;
  for (let cols = 1; cols <= 3; cols++) {
    const lay = layoutInCols(chunk, bodyPx, colW, measure, cols);
    if (lay.cols < cols) break;                        // ran out of items to split
    if (lay.w > maxW && cols > 1) break;
    if (lay.h <= maxH) return lay;
    best = lay;
  }
  return best;
}

function layoutInCols(chunk, bodyPx, colW, measure, cols) {
  const items = chunk.items || [];
  cols = Math.max(1, Math.min(cols, items.length));   // never an empty column
  if (cols === 1) return { ...layoutChunk(chunk, bodyPx, colW, measure), cols: 1 };
  const per = Math.ceil(items.length / cols);
  const parts = [];
  for (let c = 0; c < cols; c++) {
    const sub = { ...chunk, header: c === 0 ? chunk.header : undefined, items: items.slice(c * per, (c + 1) * per) };
    parts.push(layoutChunk(sub, bodyPx, colW, measure));
  }
  const gap = bodyPx * 0.4;
  const headerLines = parts[0].lines.filter(l => l.cls === 'header');
  const headerH = headerLines.length ? headerLines.at(-1).y + bodyPx * 0.3 : 0;
  let x = 0;
  const lines = [];
  for (const [c, p] of parts.entries()) {
    const dy = c === 0 ? 0 : headerH;   // later columns start below the header line
    for (const l of p.lines) lines.push({ ...l, x: l.x + x, y: l.y + dy });
    x += p.w - bodyPx * 0.9 + gap;
  }
  const w = x + bodyPx * 0.9 - gap;
  const h = Math.max(...parts.map((p, c) => p.h + (c ? headerH : 0)));
  return { w, h, lines, cols };
}

export function buildWall(versions, measure, opts = {}) {
  const panel = opts.panel || PANEL;
  // the painted text boxes' manifest (TT-1269): an intent's box takes its reach
  // from its piece when the host has the art, else from the drawn motif;
  // it rides the wall so the renderer draws with the art the solve reserved for
  const art = opts.textBoxArt || {};
  const bodyPx = opts.bodyPx || bodyPxFor(opts.expectedMinutes, panel);
  // placement grid: coarse enough that a full board layout is a few ms, not seconds
  const margin = bodyPx * 0.6, step = Math.max(18, bodyPx);
  // versionT: each version's first new stamp (Infinity when it added nothing)
  const wall = { panel, bodyPx, textBoxArt: art, boxes: [], byId: {}, layouts: [], connectors: [], spineSegs: [], frames: [], symbolsUsed: new Set(), versions: versions.length, versionT: [], dropped: [], unplacedSymbols: new Set() };
  if (!versions.length) return wall;
  const floorPx = panel.h * TYPE_FLOOR;
  const legible = (scale, k = 1) => bodyPx * scale * k >= floorPx;

  let frame = null;              // { regions, board, byName, anchor, t, v }
  let order = 0, lastReveal = 0;
  const clock = (tv, fallback, gap = ITEM_GAP) => { const v = Math.max(tv ?? fallback, lastReveal + gap); lastReveal = v; return v; };
  const addBox = b => { wall.boxes.push(b); wall.byId[b.id] = b; return b; };
  // header line gets `headerT`; each item's lines get that item's time —
  // and its sentence, and whether it is that sentence as spoken (the
  // planner's `verbatim`), so a host can make the line a link to it
  const stampLineTimes = (lines, headerT, itemT, items = []) => {
    let li = -1;
    return lines.map(ln => {
      if (ln.cls === 'header') return { ...ln, t: headerT };
      if (ln.itemStart) li++;
      const i = Math.max(0, Math.min(li, itemT.length - 1));
      const it = items[i] || {};
      return { ...ln, t: itemT[i], sent: it.sent ?? null, verbatim: !!it.verbatim };
    });
  };
  // one drawing per symbol per board; never the anchor
  const addStickers = (ch, t) => {
    for (const symbolId of ch.symbol_ids || []) {
      if (wall.symbolsUsed.has(symbolId) || symbolId === frame.anchor?.symbolId) continue;
      wall.symbolsUsed.add(symbolId);
      const id = 'sym:' + symbolId, size = stickerSize(panel);
      if (wall.byId[id]) { wall.byId[id].of = ch.id; continue; }   // orphaned by a retirement: adopt it
      addBox({ id, kind: 'symbol', symbolId, of: ch.id, w: size, h: size, t: t + 4, order: order++ });
    }
  };

  // ---- frame ----------------------------------------------------------------
  function setFrame(plan, t, vi) {
    if (frame) { frame.tEnd = t; for (const b of wall.boxes) if (b.banner && b.frameV === frame.v) b.tRetire = t; }
    const tpl = getTemplate(plan.archetype, plan.metaphor);
    const regions = tpl.regions.map(r => ({ ...r, poly: regionPolygon(r, panel) }));
    frame = { regions, board: boardPoly(panel), byName: Object.fromEntries(regions.map(r => [r.name, r])), t, v: vi, sections: plan.sections || [],
              // a template without art (the opening sketch's notes/plain) has no anchor
              anchor: tpl.anchor ? { symbolId: tpl.anchor.symbol_id, x: tpl.anchor.x * panel.w, y: tpl.anchor.y * panel.h, h: tpl.anchor.h * panel.h, opacity: tpl.anchor.opacity, t } : null };
    wall.frames.push(frame);
    for (const s of frame.sections) addBanner(s, t, vi);
  }
  // A section's banner is one box per lettering — a name, a frame, a
  // version — and stands while something sits under its section (TT-1266:
  // the opening's four banners stood over a meeting that had moved on, two
  // of them over nothing). A section that empties loses its banner, one
  // that refills or is renamed by a review gets a new one: the mount only
  // ever adds, and a scribe crosses out and letters again.
  function addBanner(s, t, vi) {
    const lay = layoutChunk({ items: [{ class: 'subheader', text: s.name }] }, bodyPx, panel.w * 0.26, measure);
    return addBox({ id: `sec:${s.id}:${vi}`, kind: 'chunk', banner: true, container: false, hue: 'accent', sid: s.id, name: s.name, region: s.region, w: lay.w, h: lay.h,
                    lines: lay.lines.map(l => ({ ...l, t })), t, tInk: t + 2, order: order++, frameV: frame.v });
  }
  function syncBanners(sections, t, vi) {
    const filled = new Set(), standing = new Map();
    for (const b of wall.boxes) {
      if (b.kind !== 'chunk' || !liveAt(b, vi)) continue;
      if (b.banner) { if (b.frameV === frame.v) standing.set(b.sid, b); } else filled.add(b.region);
    }
    for (const s of sections) {
      const sec = frame.sections.find(f => f.id === s.id);
      if (!sec) continue;
      const want = filled.has(sec.region) ? s.name : null;
      const bn = standing.get(s.id);
      if (bn && bn.name !== want) retireBox(bn, vi, t);
      if (want && bn?.name !== want) addBanner({ ...sec, name: want }, t, vi);
    }
  }

  // ---- layout engine ----------------------------------------------------------
  // a box is placed with its reach (`b.reach`, an intent's paint outside
  // the text — rays, curls, an arrowhead — zero for the rest) around the
  // text rect, so neighbours stay clear; `pos` is the text rect's corner.
  // `plain` seats a box with an intent without its paint (see the intent rule below)
  const reachOf = (b, plain) => (plain ? NO_REACH : b.reach || NO_REACH);
  const scaled = (b, s, plain) => inflate(b, reachOf(b, plain), s);
  const corner = (b, r, s, plain) => ({ x: r.x + reachOf(b, plain).left * s, y: r.y + reachOf(b, plain).top * s });
  const outer = (b, p, s) => ({ x: p.x - reachOf(b).left * s, y: p.y - reachOf(b).top * s });
  const liveAt = (b, vi) => b.retireV == null || b.retireV > vi;
  // a chunk leaves the board at t: consolidated into another (`into`), or
  // never placed at all; its stickers follow it into the chunk it was
  // folded into, or are freed for another chunk to draw
  const retireBox = (b, vi, t, into = null) => {
    b.retireV = vi; b.tRetire = t;
    for (const s of wall.boxes) if (s.kind === 'symbol' && s.of === b.id) {
      if (into && into.retireV == null) s.of = into.id; else wall.symbolsUsed.delete(s.symbolId);
    }
  };
  // a solver must never freeze the page: each version gets its own budget
  // (a live board replays its whole series — a minute-by-minute meeting is
  // dozens of versions, TT-1237); past it the version is left off like one
  // that does not fit, and the board goes on with the next
  const seriesEnd = performance.now() + SERIES_BACKSTOP_MS;
  let deadline = Infinity, calls = 0;
  class OverBudget extends Error {}
  function tryLayout(scale, prev, vi) {
    if (++calls > MAX_LAYOUTS || (calls % 8 === 0 && performance.now() > deadline))
      throw new OverBudget(`scene solver: budget exceeded (v${vi}, scale ${scale.toFixed(2)}, ${calls} layouts, last box ${wall.lastFail})`);
    const placed = [], pos = {}, rectOf = {}, plainIds = [];
    // a box seated at the placed rect `r` (its reach included unless `plain`), shrunk by its own `k`
    const seat = (b, r, k = 1, plain = false) => { placed.push(r); rectOf[b.id] = r; pos[b.id] = { ...corner(b, r, scale * k, plain), ...(k !== 1 ? { k } : {}) }; if (plain) plainIds.push(b.id); };
    const keep = (b, r, ignore = null, m = margin * scale, k = 1) => {
      if (r && !placed.some(p => p !== ignore && overlaps(r, p, m))) { seat(b, r, k); return true; }
      return false;
    };
    const core = anchorCore(frame.anchor);
    // the per-box shrinks the floor still allows at this scale
    const kIn = [0.9, 0.8].filter(k => legible(scale, k)), kOut = [1, 0.9, 0.8, 0.7].filter(k => legible(scale, k));
    for (const b of wall.boxes.filter(b => b.fixed)) { placed.push({ ...b.fixed, ...scaled(b, scale) }); pos[b.id] = { ...b.fixed }; }
    const live = wall.boxes.filter(b => b.kind === 'chunk' && liveAt(b, vi) && (!b.banner || b.frameV === frame.v)).sort((a, b) => (b.banner ? 1 : 0) - (a.banner ? 1 : 0) || a.order - b.order);
    for (const b of live) {
      const region = frame.byName[b.region] || frame.regions[0];
      const reg = b.banner ? { ...region, flow: 'top' } : region;
      // a box with an intent whose paint is not yet inked takes its intent only when
      // the paint fits in its section, clear of the anchor art's core
      // (packer.anchorCore) — at full size, else one notch smaller (0.9,
      // 0.8: TT-1269 loosening 4, the one bend of "never shrink the text
      // for a decoration"); else it is seated plain — unless its text
      // leaves the section anyway, when the paint goes along at full size
      // if it fits clear of the core (below) — and the commit makes either
      // final (`intentInked`, or the intent off the box): an intent never moves
      // the text. A box inked with its paint keeps it wherever it is
      // re-seated
      let plain = false;
      if (b.motif && !b.intentInked) {
        const szM = scaled(b, scale);
        let r = null, k = 1;
        for (k of [1, ...kIn]) {
          r = place({ w: szM.w * k, h: szM.h * k }, region.poly, placed.concat(core), reg, margin * scale, step * scale);
          if (r) break;
        }
        if (r) { seat(b, r, k); continue; }
        plain = true;
      }
      const sz = scaled(b, scale, plain);
      if (!b.banner && prev?.pos[b.id]) {
        // a box keeps its spot and its own shrink — unless the board's scale
        // has since taken that shrink under the floor, or it has grown (new
        // items, a second column) past the board's edge, when it is re-seated
        // (TT-1229: grown cards ran 59–300 units off and were clipped)
        const pk = prev.pos[b.id].k || 1;
        const held = { ...outer(b, prev.pos[b.id], scale * pk), w: sz.w * pk, h: sz.h * pk };
        if (legible(scale, pk) && rectInPoly(held, frame.board) && keep(b, held, null, margin * scale, pk)) continue;
      }
      // inside the section's region first; failing that, the nearest free spot
      // to the region (a scribe writes beside the section when it's full) —
      // banners are the section itself and never leave. A box carrying its
      // paint keeps off the anchor's core wherever it is re-seated
      const blockers = b.motif ? placed.concat(core) : placed;
      let r = place(sz, region.poly, blockers, reg, margin * scale, step * scale), k = 1;
      if (!r && !b.banner) {
        // a scribe writes smaller to stay inside the section before writing beside it
        for (k of kIn) {
          r = place({ w: sz.w * k, h: sz.h * k }, region.poly, blockers, reg, margin * scale, step * scale);
          if (r) break;
        }
      }
      if (!r && !b.banner) {
        const anchor = flowAnchor(reg, bbox(region.poly));
        // a box whose text leaves its section anyway takes its paint along,
        // full size, clear of the core (the decoration never causes the move)
        if (plain) {
          r = place(scaled(b, scale), frame.board, blockers, reg, margin * scale, step * scale, anchor);
          if (r) { seat(b, r); continue; }
        }
        // nearest free spot to the section; then this box alone a little smaller
        for (k of kOut) {
          r = place({ w: sz.w * k, h: sz.h * k }, frame.board, blockers, reg, margin * scale, step * scale, anchor);
          if (r) break;
        }
      }
      if (!r) { wall.lastFail = b.id; return null; }
      seat(b, r, k, plain);
    }
    for (const b of wall.boxes.filter(b => b.kind === 'symbol')) {
      const parent = wall.byId[b.of];
      if (!parent || !pos[parent.id] || !liveAt(parent, vi)) { pos[b.id] = null; continue; }
      const pr = rectOf[parent.id], sz = scaled(b, scale);
      // a sticker may sit on its own chunk's corner, never on anything else;
      // it tolerates the same half-margin placeNear uses, so it stays put
      const pk = prev?.pos[b.id]?.k || 1;
      const held = prev?.pos[b.id] && { x: prev.pos[b.id].x, y: prev.pos[b.id].y, w: sz.w * pk, h: sz.h * pk };
      if (held && onCorner(held, pr, margin * scale) && keep(b, held, pr, margin * scale * 0.5, pk)) {
        pos[b.id].of = parent.id;
        continue;
      }
      let r = null, k = 1;
      for (k of [1, 0.8, 0.65]) { r = placeNear(pr, sz.w * k, placed, panel, margin * scale); if (r) break; }   // a smaller sticker before none
      if (!r) { pos[b.id] = null; wall.unplacedSymbols.add(b.id); continue; }
      placed.push(r); pos[b.id] = { x: r.x, y: r.y, of: parent.id, ...(k !== 1 ? { k } : {}) };
    }
    // sizes as laid out now — a chunk that grows later must not be drawn at its final size yet
    const size = {};
    for (const b of wall.boxes) if (pos[b.id]) size[b.id] = { w: b.w, h: b.h, lines: b.lines };
    return { scale, pos, size, plainIds, coverage: placed.reduce((a, r) => a + r.w * r.h, 0) / (panel.w * panel.h) };
  }
  function commitLayout(t, vi, fresh) {
    const now = performance.now();
    if (now > seriesEnd) { console.warn(`scene solver: series budget exceeded, v${vi} left off`); return false; }
    deadline = now + BACKSTOP_MS; calls = 0;
    try { return solveLayout(t, vi, fresh); } catch (e) {
      if (!(e instanceof OverBudget)) throw e;
      console.warn(e.message);
      return false;
    } finally { if (DEBUG()) console.log(`  v${vi}: ${calls} layouts`); }
  }
  function solveLayout(t, vi, fresh) {
    const prev = fresh ? null : wall.layouts.at(-1);
    let scale = wall.layouts.at(-1)?.scale ?? 1;
    if (DEBUG()) console.log(`v${vi} commit t=${t.toFixed(0)} scale=${scale.toFixed(2)} boxes=${wall.boxes.length}`);
    let lay = tryLayout(scale, prev, vi) || tryLayout(scale, null, vi);
    while (!lay && legible(scale * SCALE_STEP)) { scale *= SCALE_STEP; if (DEBUG()) console.log(`  shrink → ${scale.toFixed(2)} fail=${wall.lastFail}`); lay = tryLayout(scale, prev, vi) || tryLayout(scale, null, vi); }
    // at the floor: a scribe leaves the least important new chunk off the
    // board rather than writing smaller — retired before it ever landed
    const added = wall.boxes.filter(b => b.kind === 'chunk' && !b.banner && b.addedV === vi && liveAt(b, vi)).sort((a, b) => a.importance - b.importance);
    for (const b of added) {
      if (lay) break;
      retireBox(b, vi, t); wall.dropped.push({ v: vi, id: b.id });
      if (DEBUG()) console.log(`  drop ${b.id} (importance ${b.importance})`);
      lay = tryLayout(scale, prev, vi) || tryLayout(scale, null, vi);
    }
    // still no room: the board makes it, as the planner does past its load —
    // the least important card that was already there leaves, never one on
    // the spine (TT-1237: a board that keeps an old layout because nothing
    // fits never updates again). Only if that makes the board fit: the
    // cards go off tentatively and come back unless a layout is found.
    if (!lay) {
      const spine = new Set(wall.spineSegs.flatMap(s => [s.from, s.to]));
      const old = wall.boxes.filter(b => b.kind === 'chunk' && !b.banner && b.addedV !== vi && liveAt(b, vi) && !spine.has(b.id))
        .sort((a, b) => a.importance - b.importance || a.order - b.order);
      const gone = [];
      try {
        for (const b of old.slice(0, MAX_MAKE_ROOM)) {
          b.retireV = vi; gone.push(b);
          if ((lay = tryLayout(scale, null, vi))) break;
        }
      } finally {
        for (const b of gone) b.retireV = undefined;   // retireBox sets it for real
      }
      if (lay) for (const b of gone) {
        retireBox(b, vi, t); wall.dropped.push({ v: vi, id: b.id, old: true });
        if (DEBUG()) console.log(`  make room: retire ${b.id} (importance ${b.importance})`);
      }
    }
    if (!lay) return false;
    // breathe back out: if the board emptied (consolidation, reframe) grow the
    // type again while everything still fits and coverage is modest
    // — one step per version, so growth is gradual and cheap
    if (lay.scale < 1 && lay.coverage < 0.45) {
      const s2 = Math.min(1, lay.scale / SCALE_STEP);
      const up = tryLayout(s2, prev, vi);
      if (up) lay = up;
    }
    // the intent decision is inked with the layout: a box seated plain loses
    // its intent for good, one seated with its paint keeps it for good
    for (const id of lay.plainIds) Object.assign(wall.byId[id], { motif: undefined, reach: NO_REACH });
    for (const b of wall.boxes) if (b.motif && lay.pos[b.id]) b.intentInked = true;
    const last = wall.layouts.at(-1);
    // a new layout whenever anything moved, rescaled, or grew (a grown chunk
    // shows its new lines only through a layout that carries them)
    const changed = !last || lay.scale !== last.scale
      || Object.keys(lay.pos).some(k => JSON.stringify(lay.pos[k]) !== JSON.stringify(last.pos[k]))
      || Object.keys(lay.size).some(k => lay.size[k].lines !== last.size[k]?.lines);
    if (changed) wall.layouts.push({ ...lay, t, v: vi, frameV: frame.v });
    return true;
  }

  // ---- title ---------------------------------------------------------------------
  const first = versions[0];
  // the title is re-lettered at most once (reframe): show the final text; the data line is the latest
  const t0 = layoutTitle({ text: versions.at(-1).title?.text || first.title?.text, date: versions.at(-1).title?.date || first.title?.date }, bodyPx, panel, measure);
  addBox({ id: '__title', kind: 'title', w: t0.w, h: t0.h, lines: t0.lines.map(l => ({ ...l, t: 0 })), t: 0, tInk: 0, fixed: { x: t0.x, y: t0.y }, order: -1 });
  setFrame(first, 0, 0);

  // ---- versions --------------------------------------------------------------------
  for (const [vi, plan] of versions.entries()) {
    if (DEBUG()) console.log(`v${vi}: ${plan.chunks.length} chunks, sections=${(plan.sections || []).length}, reframe=${!!plan.reframed_now}`);
    const times = plan._times || {};
    const vEnd = plan._window_end ?? (vi + 1) * 600;
    const vStart = vi ? (versions[vi - 1]._window_end ?? vi * 600) : 0;
    const tItem = it => (it.sent != null && times[String(it.sent)] != null) ? Math.min(vEnd, Math.max(vStart, times[String(it.sent)])) : null;
    let tFirstNew = Infinity;
    const ordered = [...plan.chunks].sort((a, b) => (b.importance || 0) - (a.importance || 0));

    // retirements (consolidation) and re-sectioning (reframe) come first;
    // they take one clock step only when there is something to do
    const retiring = plan.chunks.filter(ch => ch.retired && wall.byId[ch.id] && wall.byId[ch.id].retireV == null);
    let reframed = false;
    if ((vi && plan.reframed_now) || retiring.length) {
      const tVersion = clock(Math.min(...plan.chunks.flatMap(c => c.items.map(it => tItem(it) ?? vEnd)), vEnd), vEnd);
      if (vi && plan.reframed_now) { setFrame(plan, tVersion, vi); reframed = true; }
      for (const ch of retiring) retireBox(wall.byId[ch.id], vi, tVersion, ch.retired_into && wall.byId[ch.retired_into]);
      tFirstNew = tVersion;
    }
    for (const ch of plan.chunks) {
      const b = wall.byId[ch.id];
      if (b && ch.region && ch.region !== b.region) b.region = ch.region;   // matters on the next fresh (reframe) layout
    }

    // Reveal times first, in the order the items were spoken: the clock paces
    // them ITEM_GAP apart in that order, whatever order the chunks are seated
    // in below (importance). A version spanning the whole meeting — a batch
    // cassette's one plan (TT-1327) — would otherwise queue every chunk after
    // the most important one, past the meeting's end.
    const newOf = new Map();    // chunk id → its items new in this version
    for (const ch of plan.chunks) {
      if (ch.retired) continue;
      const known = wall.byId[ch.id];
      if (known && ch.items.length < known.itemCount) console.warn(`scene solver: ${ch.id} lost items in v${vi}; the board is append-only`);
      newOf.set(ch.id, known ? ch.items.slice(known.itemCount) : ch.items);
    }
    const stamps = new Map();   // an item, or a new chunk (its header) → reveal time
    const pending = [];
    for (const ch of plan.chunks) {
      const items = newOf.get(ch.id);
      if (!items?.length) continue;
      const at = it => tItem(it) ?? vEnd;
      if (!wall.byId[ch.id]) pending.push({ key: ch, at: Math.min(...items.map(at)), rank: 0 });   // the header, before its first item
      for (const it of items) pending.push({ key: it, at: at(it), rank: 1 });
    }
    pending.sort((a, b) => a.at - b.at || a.rank - b.rank);
    // the gap closes up when the version holds more than its window paces
    // at ITEM_GAP (a short, dense meeting in one plan), so the last thing
    // said still shows before the end
    const gap = Math.min(ITEM_GAP, (vEnd - vStart) / Math.max(1, pending.length));
    for (const p of pending) stamps.set(p.key, clock(p.at, vEnd, gap));

    for (const ch of ordered) {
      if (ch.retired) continue;
      const known = wall.byId[ch.id];
      const newItems = newOf.get(ch.id) || [];
      if (!newItems.length) continue;
      const regionH = bbox((frame.byName[ch.region] || frame.regions[0]).poly).h * 0.85;
      if (!known) {
        const maxW = chunkMaxW(ch.importance, panel);
        const lay = layoutColumns(ch, bodyPx, maxW, measure, regionH, panel.w * 0.48);
        const tHeader = stamps.get(ch);
        const perItem = ch.items.map(it => stamps.get(it));
        const lines = stampLineTimes(lay.lines, tHeader, perItem, ch.items);
        // an intent is the chunk's for good (TT-1269: the planner sets it when the chunk opens)
        const motif = chunkIntent(ch);
        const box = addBox({ id: ch.id, kind: 'chunk', w: lay.w, h: lay.h, maxW, lines, itemT: perItem, region: ch.region, hue: ch.hue || 'accent',
                             container: ch.container ?? true, itemCount: ch.items.length, t: Math.min(...lines.map(l => l.t)), tInk: Math.max(...perItem) + SECOND_PASS_DELAY, order: order++,
                             addedV: vi, importance: ch.importance || 0, motif, reach: chunkReach(motif, lay, art) });
        tFirstNew = Math.min(tFirstNew, box.t);
        addStickers(ch, perItem[0]);
      } else {
        const lay = layoutColumns({ ...ch, items: ch.items }, bodyPx, known.maxW, measure, regionH, panel.w * 0.48);
        const perNew = newItems.map(it => stamps.get(it));
        // item reveal times: old items keep theirs (by item index), new ones get the clock
        const itemT = (known.itemT || []).concat(perNew).slice(0, ch.items.length);
        const lines = stampLineTimes(lay.lines, known.lines[0]?.t ?? perNew[0], itemT, ch.items);
        const w = Math.max(known.w, lay.w);
        Object.assign(known, { h: lay.h, w, lines, itemT, itemCount: ch.items.length, tInk: Math.max(known.tInk, Math.max(...perNew) + SECOND_PASS_DELAY), reach: chunkReach(known.motif, { w, h: lay.h }, art) });
        addStickers(ch, perNew[0]);   // symbols inherited through consolidation
        tFirstNew = Math.min(tFirstNew, perNew[0]);
      }
    }
    if (Number.isFinite(tFirstNew)) syncBanners(plan.sections || [], tFirstNew, vi);
    // the last version's connectors win, as the spine's segments do: a
    // review may withdraw an arrow the meeting did not bear out (TT-1266);
    // one already drawn keeps its reveal time
    if (plan.connectors) {
      const drawn = new Map(wall.connectors.map(e => [edgeKey(e), e]));
      wall.connectors = plan.connectors.filter(c => wall.byId[c.from] && wall.byId[c.to])
        .map(c => drawn.get(edgeKey(c)) || { ...c, arrow: c.kind !== 'line', t: Math.max(wall.byId[c.from].tInk, wall.byId[c.to].tInk) });
    }
    if (plan.spine?.length) {   // the last version's spine wins; segments reveal as their chunks ink
      const ids = plan.spine.filter(id => wall.byId[id]);
      wall.spineSegs = ids.slice(1).map((id, i) => ({ from: ids[i], to: id, t: Math.max(wall.byId[ids[i]].tInk, wall.byId[id].tInk) }));
    }
    wall.versionT.push(tFirstNew);
    if (tFirstNew === Infinity) continue;
    if (!commitLayout(tFirstNew, vi, reframed)) wall.dropped.push({ v: vi });
  }
  return wall;
}

// Snapshot at time t: the active layout's positions + scale, lines revealed so far.
export function wallAt(wall, t) {
  let layout = wall.layouts[0];
  for (const l of wall.layouts) if (l.t <= t) layout = l;
  let frame = wall.frames[0];
  for (const f of wall.frames) if (f.t <= t) frame = f;
  const scale = layout?.scale ?? 1;
  const boxes = [];
  for (const b of wall.boxes) {
    if (b.tRetire != null && b.tRetire <= t) continue;
    const p = b.fixed || layout?.pos[b.id];
    if (!p) continue;
    if (b.kind === 'symbol') { if (b.t <= t) boxes.push({ ...b, x: p.x, y: p.y, scale: scale * (p.k || 1) }); continue; }
    const sz = layout?.size?.[b.id];
    const allLines = sz?.lines || b.lines;          // line positions as laid out in this layout
    const lines = allLines.filter(l => l.t <= t);
    if (!lines.length) continue;
    boxes.push({ ...b, ...(sz ? { w: sz.w, h: sz.h } : {}), x: p.x, y: p.y, scale: scale * (p.k || 1), lines, allLines, inked: b.tInk <= t });
  }
  return { boxes, scale, frame, layout, coverage: layout?.coverage ?? 0 };
}

// Every instant the board changes at, in (from, to], ascending: a line or
// sticker revealing, a text box inking, a chunk retiring, a frame or layout
// taking effect, an edge appearing. A host that paces a batch steps through
// these; `wallEnd` is the last of them.
export function wallStamps(wall, from = -Infinity, to = Infinity) {
  const set = new Set();
  const add = t => { if (t != null && t > from && t <= to && Number.isFinite(t)) set.add(t); };
  for (const b of wall.boxes) {
    add(b.t); add(b.tInk); add(b.tRetire);
    for (const ln of b.lines || []) add(ln.t);
  }
  for (const f of wall.frames) add(f.t);
  for (const l of wall.layouts) add(l.t);
  for (const e of wall.connectors) add(e.t);
  for (const e of wall.spineSegs || []) add(e.t);
  return [...set].sort((a, b) => a - b);
}

export function wallEnd(wall) {
  return wallStamps(wall).at(-1) ?? 0;
}
